import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DiscountInputRow from "../ui/DiscountInputRow.tsx";
import type { useSplitPayment } from "../hooks/useSplitPayment";
import { getGiftCardInlineError } from "../utils/toastHelpers.ts";

interface SplitDiscountRowsProps {
  splitPayment: ReturnType<typeof useSplitPayment>;
  onLayoutChange?: () => void;
}

const SplitDiscountRows: React.FC<SplitDiscountRowsProps> = ({
  splitPayment,
  onLayoutChange,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { splitDiscounts } = splitPayment;
  const {
    splitCouponCode,
    splitGiftCardAmount,
    splitPreviewDetail,
    splitDiscountWarning,
    discountsLocked,
    splitDiscountLockedReason,
    handleSplitCouponToggle,
    handleSplitCouponCodeChange,
    handleApplySplitCoupon,
    handleRemoveSplitCoupon,
    handleSplitGiftCardToggle,
    handleSplitGiftCardAmountChange,
    handleApplySplitGiftCard,
    handleRemoveSplitGiftCard,
  } = splitDiscounts;

  const normalizedCouponCode = splitCouponCode.trim();
  const normalizedGiftCardAmount = Math.max(0, Number(splitGiftCardAmount) || 0);
  const isSplitCouponApplied = Boolean(
    normalizedCouponCode &&
    splitPreviewDetail?.coupon_code_applied &&
    String(splitPreviewDetail?.coupon_code ?? "").trim() === normalizedCouponCode
  );
  const isSplitGiftCardApplied = Boolean(
    normalizedGiftCardAmount > 0 &&
    Number.isInteger(Number(splitGiftCardAmount)) &&
    Number(splitPreviewDetail?.gift_card_amount || 0) === normalizedGiftCardAmount
  );
  const hasAppliedSplitDiscount = isSplitCouponApplied || isSplitGiftCardApplied;
  const appliedSplitDiscountCount = Number(isSplitCouponApplied) + Number(isSplitGiftCardApplied);

  const giftCardInlineError = getGiftCardInlineError(t, splitGiftCardAmount);
  const resolvedSplitWarning = giftCardInlineError || splitDiscountWarning;

  useEffect(() => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        onLayoutChange?.();
      });
    });
  }, [isOpen, onLayoutChange]);

  return (
    <div className={`split-discount-collapsible ${isOpen ? "is-open" : ""}`}>
      <button
        type="button"
        className={`split-discount-collapse-trigger ${discountsLocked ? "is-locked" : ""}`}
        disabled={discountsLocked}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="split-discount-collapse-copy">
          <i className="ti ti-tag"></i>
          <span>
            {hasAppliedSplitDiscount
              ? t("label_discount_applied")
              : t("label_add_coupon_or_gift_card")}
          </span>
        </span>

        <span className="split-discount-collapse-side">
          {hasAppliedSplitDiscount && (
            <span className="split-discount-count">
              {appliedSplitDiscountCount}
            </span>
          )}

          <i className="ti ti-chevron-down"></i>
        </span>
      </button>

      {discountsLocked && (
        <p className="split-discount-locked-hint">
          {splitDiscountLockedReason}
        </p>
      )}

      {isOpen && !discountsLocked && (
        <div className="split-discount-collapse-body">
          <DiscountInputRow
            label={t("label_coupon_code")}
            placeholder={t("label_enter_coupon")}
            value={splitCouponCode}
            isApplied={isSplitCouponApplied}
            onApply={handleApplySplitCoupon}
            onRemove={handleRemoveSplitCoupon}
            onChange={(val) => {
              handleSplitCouponToggle(true);
              handleSplitCouponCodeChange(val);
            }}
            isApplyDisabled={isSplitCouponApplied || !splitCouponCode?.trim()}
            applyLabel={t("label_apply")}
            appliedLabel={t("label_applied")}
            removeLabel={t("remove")}
          />

          <DiscountInputRow
            label={t("label_gift_card")}
            placeholder={t("enter_gift_card_amount")}
            value={splitGiftCardAmount}
            isApplied={isSplitGiftCardApplied}
            onApply={handleApplySplitGiftCard}
            onRemove={handleRemoveSplitGiftCard}
            onChange={(val) => {
              handleSplitGiftCardToggle(true);
              handleSplitGiftCardAmountChange(val);
            }}
            isMoneyInput
            isApplyDisabled={
              isSplitGiftCardApplied ||
              !splitGiftCardAmount ||
              !!giftCardInlineError ||
              Number(splitGiftCardAmount) <= 0
            }
            error={resolvedSplitWarning ? <span>{resolvedSplitWarning}</span> : undefined}
            applyLabel={t("label_apply")}
            appliedLabel={t("label_applied")}
            removeLabel={t("remove")}
          />
        </div>
      )}
    </div>
  );
};

export default SplitDiscountRows;
